import { DashboardDataCard, DateDistanceDisplay, SimpleBarChart } from '@ygtechnologies/bygr-frontend-component-library';
import { capitalize, startCase } from 'lodash';
import React, { useState } from 'react';
import {
    getChangeRequestStatistics,
    getCommentMessageStatistics,
    getCommentThreadStatistics,
    getSpending,
} from 'services/statistics';
import { useAsyncState } from 'utils/hooks';

interface StatisticsDashboardProps {
    projectIds: number[];
    // project: BygrProject;
}

const StatisticsSharedCards: React.FC<StatisticsDashboardProps> = ({ projectIds }) => {
    // Does not use it on bygr level. Only on project level
    const shouldLoadChangeRequests = projectIds.length > 0;
    const [changeRequestStatistics] = useAsyncState(
        shouldLoadChangeRequests ? () => getChangeRequestStatistics(projectIds) : () => Promise.resolve(null),
        projectIds,
        null
    );

    const [ctStats] = useAsyncState(() => getCommentThreadStatistics(projectIds), projectIds, null);
    const [cmStats] = useAsyncState(() => getCommentMessageStatistics(projectIds), projectIds, null);
    const [spending] = useAsyncState(() => getSpending(projectIds), projectIds, null);
    const [showSpendingTotal, setShowSpendingTotal] = useState(true);

    function createRows<T extends {}>(data: T, ...excludeKeys: (keyof T)[]) {
        return Object.keys(data)
            .filter((k) => !(excludeKeys as string[]).includes(k))
            .map((k) => ({
                name: capitalize(startCase(k)),
                value: data[k],
            }));
    }

    function numberWithCommas(x) {
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    return (
        <>
            {shouldLoadChangeRequests && (
                <DashboardDataCard rowSpan={2} title="Change requests by status" loading={!changeRequestStatistics}>
                    {() => (
                        <>
                            <SimpleBarChart
                                data={createRows(changeRequestStatistics!, 'total')}
                                nameKey="name"
                                valueKey="value"
                            />
                            <small>Total: {numberWithCommas(changeRequestStatistics?.total)}</small>
                        </>
                    )}
                </DashboardDataCard>
            )}

            <DashboardDataCard rowSpan={2} title="Threads" loading={!ctStats}>
                {() => (
                    <>
                        <SimpleBarChart data={createRows(ctStats!, 'total')} nameKey="name" valueKey="value" />
                        <small>Total: {numberWithCommas(ctStats?.total)}</small>
                    </>
                )}
            </DashboardDataCard>
            <DashboardDataCard rowSpan={1} title="Messages" loading={!cmStats}>
                {() => (
                    <>
                        <SimpleBarChart
                            data={createRows(cmStats!, 'total', 'lastSentMessage')}
                            nameKey="name"
                            valueKey="value"
                        />
                        <small>
                            Last message sent: <DateDistanceDisplay value={cmStats!.lastSentMessage} />
                        </small>
                        <small>Total: {numberWithCommas(cmStats?.total)}</small>
                    </>
                )}
            </DashboardDataCard>
            <DashboardDataCard
                onClick={() => setShowSpendingTotal(!showSpendingTotal)}
                rowSpan={1}
                title={`Spending - ${showSpendingTotal ? 'Total' : 'Average'}`}
                loading={!spending}>
                {() => (
                    <>
                        {showSpendingTotal ? (
                            <>
                                <SimpleBarChart
                                    data={createRows(
                                        spending!,
                                        'total',
                                        'totalAverage',
                                        'averageBuyerOptionSpendingPerUnit',
                                        'averageChangeRequestSpendingPerUnit'
                                    )}
                                    nameKey="name"
                                    valueKey="value"
                                />
                                <small>Total: {numberWithCommas(spending?.total.toFixed(1))}</small>
                            </>
                        ) : (
                            <>
                                <SimpleBarChart
                                    data={createRows(spending!, 'total', 'totalAverage', 'buyerOptions', 'changeRequests')}
                                    nameKey="name"
                                    valueKey="value"
                                />
                                <small>Total average: {numberWithCommas(spending?.totalAverage.toFixed(1))}</small>
                            </>
                        )}
                    </>
                )}
            </DashboardDataCard>
        </>
    );
};

export default StatisticsSharedCards;
